import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import {
  FaArrowLeft,
  FaCheck,
  FaClock,
  FaRupeeSign,
  FaFileAlt,
  FaUserInjured,
  FaDownload,
  FaExclamationTriangle,
  FaLock,
  FaNotesMedical,
  FaStethoscope,
  FaPlus,
} from "react-icons/fa";

import { Button, Badge, Input } from "../../../Components/UI";
import CustomSpinner from "../../../Components/CommonComponents/CustomSpinner";
import CameraCapture from "../../../Components/CommonComponents/CameraCapture";
import { END_POINT, getJwtToken } from "../../../Redux/AdminReducer/action";
import { useGetLoggedInUser } from "../../../UtilityHookFunctions/LoggedInUser";
import { DownloadFile } from "../../../UtilityHookFunctions/OtherMongoHelperFunctions";

const errorText = (error) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  "Something went wrong";

const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
};

const fetchItems = async (path) => {
  try {
    return await axios.get(`${END_POINT}/${path}`, {
      headers: { Authorization: getJwtToken() },
    });
  } catch (error) {
    toast.error(errorText(error));
  }
};

const saveBid = async (data, nav, bidId, setIsSubmitting) => {
  setIsSubmitting(true);
  const toastId = toast.loading(bidId ? "Updating bid proposal..." : "Submitting bid proposal...");
  try {
    const config = { headers: { Authorization: getJwtToken() } };
    if (bidId) {
      await axios.patch(`${END_POINT}/bid/update/${bidId}`, data, config);
    } else {
      await axios.post(`${END_POINT}/bid/`, data, config);
    }
    toast.dismiss(toastId);
    toast.success(
      bidId ? "Bid proposal updated successfully!" : "Bid proposal placed successfully!",
      { autoClose: 2000 }
    );
    setTimeout(() => nav("/Bids"), 1000);
  } catch (error) {
    toast.dismiss(toastId);
    toast.error(errorText(error), { autoClose: 3500 });
  } finally {
    setIsSubmitting(false);
  }
};

const AddBids = () => {
  const { bid_id: therapyId } = useParams();
  const [formData, setFormData] = useState({});
  const [therapy, setTherapy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [replacementFile, setReplacementFile] = useState(null);
  const fileInputRef = useRef(null);

  const nav = useNavigate();
  const loggedInUser = useGetLoggedInUser();

  useEffect(() => {
    setLoading(true);
    const p1 = fetchItems(`alternative_therapy/getForm/${therapyId}`).then((e) =>
      setTherapy(e?.data || null)
    );

    let p2 = Promise.resolve();
    if (loggedInUser?._id) {
      p2 = fetchItems(`bid/auction/${therapyId}/${loggedInUser._id}`).then((e) => {
        if (e?.data?._id) setFormData(e.data);
      });
    }

    Promise.all([p1, p2]).finally(() => setLoading(false));
  }, [therapyId, loggedInUser?._id]);

  const isUpdate = Boolean(formData?._id);
  const locked =
    (isUpdate && formData?.status !== "bid") || (therapy && therapy.status !== "bid");

  const handleInputChange = (e) => {
    const { value, name } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAppendTemplate = (templateText) => {
    setFormData((prev) => {
      const current = prev.description ? prev.description + "\n\n" : "";
      return { ...prev, description: current + templateText };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !(Number(formData?.duration) > 0) ||
      !(Number(formData?.bidPrice) >= 0) ||
      formData?.bidPrice === "" ||
      formData?.bidPrice == null
    ) {
      toast.error("Please enter a valid duration (days) and price (₹)");
      return;
    }
    const iformdata = new FormData();
    iformdata.append("duration", formData.duration);
    iformdata.append("bidPrice", formData.bidPrice);
    if (formData?.description) {
      iformdata.append("description", formData.description);
    }
    iformdata.append("bidOn", therapyId);
    if (replacementFile) {
      iformdata.append("file", replacementFile);
    }
    await saveBid(iformdata, nav, formData?._id, setIsSubmitting);
  };

  const requestForm = therapy?.requestForm || {};
  const perDayRate =
    Number(formData?.duration) > 0 && Number(formData?.bidPrice) > 0
      ? Math.round(Number(formData.bidPrice) / Number(formData.duration))
      : null;

  if (loading) {
    return (
      <div className="w-full min-h-[60vh] flex flex-col items-center justify-center p-8">
        <CustomSpinner />
        <p className="mt-4 text-sm text-slate-500">Loading therapy request details...</p>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6 p-4 md:p-8">
      {/* Top Header Card */}
      <div className="flex flex-col gap-4 rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => nav(-1)}
            icon={<FaArrowLeft className="h-3.5 w-3.5" />}
          >
            Back
          </Button>

          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-xl font-bold tracking-tight text-slate-800">
                {isUpdate ? "Update Your Bid Proposal" : "Submit Bid Proposal"}
              </h1>
              {therapy?.therapy && (
                <Badge variant="primary" className="capitalize">
                  {therapy.therapy}
                </Badge>
              )}
              {isUpdate && <Badge variant="purple">Existing Proposal</Badge>}
              {locked && (
                <Badge variant="warning">
                  <FaLock className="mr-1 h-3 w-3" /> Bidding Closed
                </Badge>
              )}
            </div>
            <p className="mt-0.5 text-xs text-slate-500">
              Alternative Therapies · Submit commercial terms and clinical protocol for this therapy assignment
            </p>
          </div>
        </div>

        {therapy?._id && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => nav(`/Bids/${therapy._id}`)}
          >
            View Therapy Details
          </Button>
        )}
      </div>

      {/* Locked Alert if proposal is decided or request closed */}
      {locked && (
        <div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-amber-800 shadow-sm">
          <FaExclamationTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <div className="text-xs sm:text-sm">
            <p className="font-bold">Bidding is Closed</p>
            <p className="mt-0.5 text-amber-700">
              {isUpdate && formData?.status !== "bid"
                ? `This bid has already been decided (${
                    formData?.status === "win" ? "Won & Assigned" : "Not Selected"
                  }) and can no longer be edited.`
                : "This therapy request is no longer accepting new or updated proposals."}
            </p>
          </div>
        </div>
      )}

      {/* 2-Column Clinical Workspace: Left = Form, Right = Patient Brief (Sticky) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Left Column (7 cols on lg, 8 on xl): The Big Form */}
        <div className="lg:col-span-7 xl:col-span-8 space-y-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Section 1: Commercial & Timeline Terms */}
            <div className="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm space-y-6">
              <div className="border-b border-slate-100 pb-3 flex items-center justify-between">
                <div>
                  <h2 className="text-sm font-bold text-slate-800">
                    1. Proposed Timeline &amp; Commercial Terms
                  </h2>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Enter the total course duration in days and your proposed consulting fee.
                  </p>
                </div>
                {perDayRate && (
                  <Badge variant="success" size="sm">
                    ≈ ₹{perDayRate.toLocaleString("en-IN")} / day
                  </Badge>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <Input
                    label="Proposed Duration (in Days)"
                    type="number"
                    min="1"
                    required
                    name="duration"
                    placeholder="e.g. 15"
                    value={formData?.duration ?? ""}
                    onChange={handleInputChange}
                    leadingIcon={<FaClock />}
                    disabled={locked}
                  />
                  <div className="mt-1.5 flex items-center justify-between text-xs text-slate-500">
                    <span>
                      Doctor requested:{" "}
                      <strong className="text-slate-700">
                        {therapy?.duration ? `${therapy.duration} days` : "—"}
                      </strong>
                    </span>
                    {therapy?.duration && !locked && (
                      <button
                        type="button"
                        onClick={() =>
                          setFormData((p) => ({ ...p, duration: therapy.duration }))
                        }
                        className="text-sky-600 hover:text-sky-700 font-semibold hover:underline"
                      >
                        Match ({therapy.duration}d)
                      </button>
                    )}
                  </div>
                </div>

                <div>
                  <Input
                    label="Total Proposed Fee (₹)"
                    type="number"
                    min="0"
                    required
                    name="bidPrice"
                    placeholder="e.g. 4500"
                    value={formData?.bidPrice ?? ""}
                    onChange={handleInputChange}
                    leadingIcon={<FaRupeeSign />}
                    disabled={locked}
                  />
                  <div className="mt-1.5 flex items-center justify-between text-xs text-slate-500">
                    <span>
                      Patient Budget:{" "}
                      <strong className="text-slate-700">
                        {therapy?.price ? `₹${therapy.price}` : "Flexible"}
                      </strong>
                    </span>
                    {therapy?.price && !locked && (
                      <button
                        type="button"
                        onClick={() =>
                          setFormData((p) => ({ ...p, bidPrice: therapy.price }))
                        }
                        className="text-sky-600 hover:text-sky-700 font-semibold hover:underline"
                      >
                        Match Budget
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Section 2: Clinical Protocol & Strategy */}
            <div className="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm space-y-4">
              <div className="border-b border-slate-100 pb-3 flex items-center justify-between">
                <div>
                  <h2 className="text-sm font-bold text-slate-800">
                    2. Clinical Protocol &amp; Treatment Plan
                  </h2>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Detail your treatment methodology, session milestones, and expected therapeutic outcomes.
                  </p>
                </div>
              </div>

              {!locked && (
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  <span className="text-xs text-slate-400 font-medium">Quick Starters:</span>
                  <button
                    type="button"
                    onClick={() =>
                      handleAppendTemplate(
                        "• Treatment Protocol:\n  - Phase 1: Intake & Assessment\n  - Phase 2: Active Therapeutic Sessions\n  - Phase 3: Lifestyle & Diet Integration"
                      )
                    }
                    className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 text-xs text-slate-600 hover:bg-slate-100 hover:text-sky-600 transition"
                  >
                    <FaPlus className="h-2.5 w-2.5" /> Protocol Phases
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      handleAppendTemplate(
                        "• Session Schedule:\n  - Frequency: 2 sessions per week\n  - Follow-up: Weekly milestone evaluation"
                      )
                    }
                    className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 text-xs text-slate-600 hover:bg-slate-100 hover:text-sky-600 transition"
                  >
                    <FaPlus className="h-2.5 w-2.5" /> Session Schedule
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      handleAppendTemplate(
                        "• Expected Outcome:\n  - Measurable symptom reduction within first 10 days\n  - Sustainable self-management by completion"
                      )
                    }
                    className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 text-xs text-slate-600 hover:bg-slate-100 hover:text-sky-600 transition"
                  >
                    <FaPlus className="h-2.5 w-2.5" /> Expected Outcome
                  </button>
                </div>
              )}

              <div>
                <textarea
                  id="description"
                  name="description"
                  rows={6}
                  value={formData?.description ?? ""}
                  onChange={handleInputChange}
                  disabled={locked}
                  placeholder="Outline your proposed clinical strategy, therapeutic remedies, consultation cadence, and why this plan fits the patient..."
                  className="w-full rounded-xl border border-slate-200 hover:border-slate-300 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 bg-white p-3.5 text-sm text-slate-800 placeholder:text-slate-400 transition-all outline-none leading-relaxed disabled:bg-slate-100 disabled:cursor-not-allowed"
                />
                <div className="mt-1.5 flex justify-between text-xs text-slate-400">
                  <span>Formatting: plain text or bullet points supported</span>
                  <span>{formData?.description?.length || 0} characters</span>
                </div>
              </div>
            </div>

            {/* Section 3: Proposal Document Attachment */}
            <div className="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm space-y-4">
              <div className="border-b border-slate-100 pb-3 flex items-center justify-between">
                <div>
                  <h2 className="text-sm font-bold text-slate-800">
                    3. Proposal Document / Clinical Sample (Optional)
                  </h2>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Attach reference case studies, sample therapy regimens, or verification documents.
                  </p>
                </div>
                {replacementFile && (
                  <Badge variant="success" size="sm">
                    New File Ready
                  </Badge>
                )}
              </div>

              {/* Currently Attached File (if exists) */}
              {formData?.sample?.name && (
                <div
                  className={`p-4 rounded-xl border transition ${
                    replacementFile
                      ? "border-amber-200 bg-amber-50/60"
                      : "border-slate-200 bg-slate-50"
                  }`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div
                        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
                          replacementFile
                            ? "bg-amber-100 text-amber-700"
                            : "bg-sky-100 text-sky-600"
                        }`}
                      >
                        <FaFileAlt className="h-4 w-4" />
                      </div>
                      <div className="min-w-0">
                        <p
                          className={`truncate text-xs font-semibold ${
                            replacementFile
                              ? "line-through text-slate-500"
                              : "text-slate-800"
                          }`}
                          title={formData.sample.name}
                        >
                          {formData.sample.name}
                        </p>
                        <p className="text-[11px] text-slate-500 mt-0.5">
                          {replacementFile
                            ? "Will be replaced by new file upon saving"
                            : "Currently attached document on this bid"}
                        </p>
                      </div>
                    </div>
                    {formData?.sample?.id && (
                      <Button
                        type="button"
                        variant="outline"
                        size="xs"
                        onClick={() =>
                          DownloadFile(
                            "bid/download",
                            formData.sample.id,
                            formData.sample.name
                          )
                        }
                        icon={<FaDownload className="h-3 w-3" />}
                      >
                        Download
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* Newly Selected Replacement File Card */}
              {replacementFile && (
                <div className="p-4 rounded-xl border border-emerald-200 bg-emerald-50/70 space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700">
                        <FaFileAlt className="h-4 w-4" />
                      </div>
                      <div className="min-w-0">
                        <p
                          className="truncate text-xs font-bold text-emerald-900"
                          title={replacementFile.name}
                        >
                          {replacementFile.name}
                        </p>
                        <p className="text-[11px] text-emerald-700 font-medium mt-0.5">
                          {formatFileSize(replacementFile.size)} · Ready to upload
                        </p>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="xs"
                      onClick={() => {
                        setReplacementFile(null);
                        if (fileInputRef.current) fileInputRef.current.value = "";
                      }}
                      className="text-rose-600 hover:bg-rose-50"
                    >
                      Remove
                    </Button>
                  </div>
                  <p className="text-[11px] text-emerald-800">
                    ✓ This new file will replace the previous attachment once you click{" "}
                    <strong>{isUpdate ? "Update Bid Proposal" : "Submit Bid Proposal"}</strong>.
                  </p>
                </div>
              )}

              <div>
                <label
                  htmlFor="file-upload"
                  className="block text-xs font-semibold text-slate-700 mb-1.5"
                >
                  {formData?.sample?.name
                    ? replacementFile
                      ? "Change Selected File"
                      : "Choose New File to Replace Current Document"
                    : "Upload File"}
                </label>
                <input
                  id="file-upload"
                  ref={fileInputRef}
                  type="file"
                  name="file"
                  disabled={locked}
                  onChange={(e) => {
                    const f = e.target.files?.[0] || null;
                    setReplacementFile(f);
                  }}
                  className="block w-full border border-slate-200 shadow-sm rounded-xl text-xs text-slate-600 file:bg-slate-50 file:border-0 file:me-4 file:py-2.5 file:px-4 file:font-semibold file:text-slate-700 hover:file:bg-slate-100 transition disabled:opacity-50"
                />
                <CameraCapture
                  className="mt-2"
                  disabled={locked}
                  onCapture={(file) => {
                    if (fileInputRef.current) fileInputRef.current.value = "";
                    setReplacementFile(file);
                  }}
                />
                <p className="mt-1.5 text-xs text-slate-400">
                  Supported formats: PDF, DOCX, PNG, JPG (max 10MB)
                </p>
              </div>
            </div>

            {/* Section 4: Action Footer */}
            <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="text-xs text-slate-500">
                <span>Quoted: </span>
                <strong className="text-slate-800">
                  {formData?.bidPrice ? `₹${Number(formData.bidPrice).toLocaleString("en-IN")}` : "₹0"}
                </strong>
                <span> for </span>
                <strong className="text-slate-800">
                  {formData?.duration ? `${formData.duration} days` : "0 days"}
                </strong>
              </div>

              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  size="md"
                  onClick={() => nav(-1)}
                >
                  Cancel
                </Button>

                {!locked && (
                  <Button
                    type="submit"
                    variant="primary"
                    size="md"
                    isLoading={isSubmitting}
                    icon={<FaCheck className="h-3.5 w-3.5" />}
                  >
                    {isUpdate ? "Update Bid Proposal" : "Submit Bid Proposal"}
                  </Button>
                )}
              </div>
            </div>
          </form>
        </div>

        {/* Right Column (5 cols on lg, 4 on xl): Sticky Clinical Context & Patient Brief */}
        <div className="lg:col-span-5 xl:col-span-4 sticky top-6 space-y-5">
          <div className="rounded-2xl border border-slate-200/80 bg-white shadow-sm overflow-hidden">
            <div className="border-b border-slate-100 px-5 py-4 flex items-center justify-between bg-slate-50/50">
              <div className="flex items-center gap-2">
                <FaUserInjured className="text-sky-600 h-4 w-4" />
                <h2 className="text-sm font-bold text-slate-800">Patient Case Reference</h2>
              </div>
              <Badge variant="default" size="sm">
                Case Info
              </Badge>
            </div>

            <div className="p-5 space-y-4">
              {/* Patient Basic Info */}
              <div className="rounded-xl border border-slate-100 bg-slate-50 p-3.5 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-slate-400 uppercase">Patient</span>
                  <span className="text-xs font-bold text-slate-800">
                    {requestForm.fullName || "Confidential Patient"}
                  </span>
                </div>
                {requestForm.mobile && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-slate-400 uppercase">Contact</span>
                    <span className="text-xs text-slate-700">{requestForm.mobile}</span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-slate-400 uppercase">Therapy</span>
                  <span className="text-xs font-bold text-sky-700 capitalize">
                    {therapy?.therapy || "—"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-slate-400 uppercase">Requested Duration</span>
                  <span className="text-xs font-bold text-slate-800">
                    {therapy?.duration ? `${therapy.duration} days` : "—"}
                  </span>
                </div>
              </div>

              {/* Doctor Note */}
              {therapy?.note && (
                <div className="rounded-xl border border-sky-100 bg-sky-50/60 p-3.5">
                  <span className="text-xs font-bold uppercase tracking-wider text-sky-800 flex items-center gap-1.5 mb-1">
                    <FaStethoscope className="h-3 w-3" /> Doctor Note
                  </span>
                  <p className="text-xs text-slate-800 leading-relaxed">{therapy.note}</p>
                </div>
              )}

              {/* Clinical Intake Information */}
              <div className="space-y-3 pt-1">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                  <FaNotesMedical className="h-3 w-3" /> Clinical Intake
                </span>

                {requestForm.chiefComplaint && (
                  <div className="rounded-lg border border-slate-100 bg-slate-50/50 p-3">
                    <p className="text-[11px] font-semibold text-slate-400 uppercase">Reason for Referral</p>
                    <p className="text-xs text-slate-800 mt-0.5">{requestForm.chiefComplaint}</p>
                  </div>
                )}

                {requestForm.medicalHistory && (
                  <div className="rounded-lg border border-slate-100 bg-slate-50/50 p-3">
                    <p className="text-[11px] font-semibold text-slate-400 uppercase">Medical History</p>
                    <p className="text-xs text-slate-800 mt-0.5">{requestForm.medicalHistory}</p>
                  </div>
                )}

                {requestForm.currentMedication && (
                  <div className="rounded-lg border border-slate-100 bg-slate-50/50 p-3">
                    <p className="text-[11px] font-semibold text-slate-400 uppercase">Current Medication</p>
                    <p className="text-xs text-slate-800 mt-0.5">{requestForm.currentMedication}</p>
                  </div>
                )}

                {requestForm.treatmentGoal && (
                  <div className="rounded-lg border border-slate-100 bg-slate-50/50 p-3">
                    <p className="text-[11px] font-semibold text-slate-400 uppercase">Treatment Goal</p>
                    <p className="text-xs text-slate-800 mt-0.5">{requestForm.treatmentGoal}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddBids;
